import React from "react";
import { Send } from "lucide-react";
import { FILTER_DEFS } from "./rosterTagDefs";

/**
 * The prompt that turns a filtered roster into one action.
 *
 * Filtering to "everyone whose diet plan is overdue" and then opening each chat
 * in turn is the work this is meant to remove — the whole point of the filter is
 * that the same message applies to all of them.
 */

const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * Names what the coach is looking at, e.g. "3 clients are overdue a diet plan".
 * Falls back to a generic line when several filters are combined, rather than
 * stitching together a sentence that reads worse than saying nothing.
 */
function describeMatches(activeInsightIds, matchCount) {
  const subject = `${plural(matchCount, "client")} ${matchCount === 1 ? "is" : "are"}`;
  if (activeInsightIds.length !== 1) {
    return `${subject} in this filter`;
  }
  const label = FILTER_DEFS[activeInsightIds[0]]?.label || "";
  return `${subject} ${label.toLowerCase()}`;
}

/**
 * @param {Object} props
 * @param {string[]} props.activeInsightIds - Coaching filters currently on.
 * @param {string[]} props.matchedClientIds
 * @param {number} props.totalCount - Roster size before filtering.
 * @param {() => void} props.onClearFilters
 * @param {(clientIds: string[]) => void} props.onMessageAll
 */
export function NudgeCard({
  activeInsightIds,
  matchedClientIds,
  totalCount,
  onClearFilters,
  onMessageAll,
}) {
  const count = matchedClientIds.length;
  if (!count || activeInsightIds.length === 0) return null;

  return (
    <div className="mx-3 mt-4 rounded-2xl border border-dashed border-[#DAD8D2] bg-[#FBFBFB] p-4 text-center">
      <p className="text-[13.5px] font-bold text-gray-900">
        {describeMatches(activeInsightIds, count)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-bc-muted">
        Send them all the same nudge instead of opening each chat.
      </p>
      <div className="mt-3.5 flex justify-center gap-2">
        <button
          type="button"
          onClick={() => onMessageAll(matchedClientIds)}
          className="inline-flex items-center gap-1.5 rounded-full bg-orange-primary px-[15px] py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-[#E2560F]"
        >
          <Send aria-hidden="true" className="h-3.5 w-3.5" />
          Message {count === 1 ? "them" : `these ${count}`}
        </button>
        <button
          type="button"
          onClick={onClearFilters}
          className="rounded-full border border-gray-200 bg-white px-[15px] py-2 text-[12.5px] font-semibold text-gray-700 transition-colors hover:border-gray-300"
        >
          Show all {totalCount}
        </button>
      </div>
    </div>
  );
}

export default NudgeCard;
